import type { UpgradeHint } from '@snap-platform/shared';

// --- Constants ---

const PER_TYPE_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours
const GLOBAL_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours
const GLOBAL_MAX_PER_WINDOW = 2;

// Storage keys
const COOLDOWN_PREFIX = 'cg_hint_cooldown_';
const GLOBAL_TIMESTAMPS_KEY = 'cg_hint_shown_timestamps';
const SESSION_FLAG_KEY = 'cg_hint_shown_this_session';

// Type-level priority ranking (lower = higher priority)
// Risk-driven: Crypto Intel risk signals take priority over quota limits
const TYPE_RANK: Record<UpgradeHint['type'], number> = {
  risk_protection: 1,
  high_value_protection: 2,
  usage_limit: 3,
  feature_gate: 4,
  version_update: 5,
};

const PRIORITY_RANK: Record<UpgradeHint['priority'], number> = {
  high: 1,
  medium: 2,
  low: 3,
};

// --- Pure selection ---

/**
 * Returns the highest-priority hint from the array, or null if empty.
 * Does not mutate the input array.
 */
export function selectHintToShow(hints: UpgradeHint[]): UpgradeHint | null {
  if (hints.length === 0) return null;

  const sorted = [...hints].sort((a, b) => {
    const typeDiff = TYPE_RANK[a.type] - TYPE_RANK[b.type];
    if (typeDiff !== 0) return typeDiff;
    return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  });

  return sorted[0];
}

// --- Eligibility checks ---

/**
 * Checks session cap + 24hr global cap.
 */
export function isHintEligible(): boolean {
  // Session-level: max 1 per session
  if (sessionStorage.getItem(SESSION_FLAG_KEY)) return false;

  // Global 24hr cap
  const raw = localStorage.getItem(GLOBAL_TIMESTAMPS_KEY);
  if (raw) {
    try {
      const timestamps: number[] = JSON.parse(raw);
      const now = Date.now();
      const recent = timestamps.filter((t) => now - t < GLOBAL_WINDOW_MS);
      if (recent.length >= GLOBAL_MAX_PER_WINDOW) return false;
    } catch {
      // Corrupted data — allow
    }
  }

  return true;
}

/**
 * Returns true if the hint type is still within the 6hr cooldown (should NOT be shown).
 * Returns false if the cooldown has expired or never been set (CAN be shown).
 */
export function isHintTypeCooledDown(type: UpgradeHint['type']): boolean {
  const stored = localStorage.getItem(COOLDOWN_PREFIX + type);
  if (!stored) return false;

  const elapsed = Date.now() - Number(stored);
  return elapsed < PER_TYPE_COOLDOWN_MS;
}

// --- Main entry ---

/**
 * Checks all eligibility gates, filters cooled-down types, and returns the best hint.
 */
export function selectEligibleHint(hints: UpgradeHint[]): UpgradeHint | null {
  if (!isHintEligible()) return null;

  const eligible = hints.filter((h) => !isHintTypeCooledDown(h.type));
  return selectHintToShow(eligible);
}

// --- Recording ---

/**
 * Updates all 3 storage layers after showing a hint.
 */
export function recordHintShown(hint: UpgradeHint): void {
  // Per-type cooldown
  localStorage.setItem(COOLDOWN_PREFIX + hint.type, String(Date.now()));

  // Global timestamps
  const raw = localStorage.getItem(GLOBAL_TIMESTAMPS_KEY);
  let timestamps: number[] = [];
  if (raw) {
    try {
      timestamps = JSON.parse(raw);
    } catch {
      timestamps = [];
    }
  }
  timestamps.push(Date.now());
  localStorage.setItem(GLOBAL_TIMESTAMPS_KEY, JSON.stringify(timestamps));

  // Session flag
  sessionStorage.setItem(SESSION_FLAG_KEY, '1');
}

/**
 * No-op hook for future dismiss behavior tracking.
 */
export function recordHintDismissed(): void {
  // Reserved for future use
}

// --- Test utilities ---

/**
 * Test-only: clears all hint-related storage.
 */
export function _resetAllCooldowns(): void {
  // Clear per-type cooldowns
  for (const type of Object.keys(TYPE_RANK)) {
    localStorage.removeItem(COOLDOWN_PREFIX + type);
  }
  localStorage.removeItem(GLOBAL_TIMESTAMPS_KEY);
  sessionStorage.removeItem(SESSION_FLAG_KEY);
}
